import React, { useState, useEffect } from 'react'
import ListGroup from 'react-bootstrap/ListGroup'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { shallow } from 'zustand/shallow'

import Modal from '@mui/material/Modal'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'

import { useAirdropState } from 'src/contexts/airDropContext'
import { executeSelectedTransactions, getEvmGasEstimate } from '../../actions/blockchainActions'
import { txType } from 'src/pages/AirDrop/types'
import SectionTitle from 'src/components/kit/SectionTitle'
import { networksData, chainsDataType, networkType } from 'src/util/chainsData'
import { tokenSelectObjectType } from 'src/util/types'
import { formatAddressEllipsisShort } from 'src/util/formatAddressEllipsis'
import { useRpcCountStore } from 'src/store/rpcCount'
import UiButton from 'src/ui/atoms/Button'
import './ConfirmExecuteModal.scss'

export default function ConfirmExecuteModal({
  showExecuteModal,
  toggleExecuteModal,
  validTransactions,
  clearSelectedTransactions,
  nativeCurrencyBalance,
  chain,
  network,
  selectedTokenBalance,
  selectedToken,
  initiator,
  isWalletConnected,
}: {
  showExecuteModal: boolean
  toggleExecuteModal: () => void
  validTransactions: txType[]
  clearSelectedTransactions: () => void
  nativeCurrencyBalance: any
  chain: keyof chainsDataType
  network: networkType
  selectedTokenBalance: number
  selectedToken: tokenSelectObjectType
  initiator: string
  isWalletConnected: boolean
}) {
  const airdropState = useAirdropState()
  const [gas, setGas] = useState<number>(0)
  const [isLoading, setIsLoading] = useState(false)

  const isMobile = window.innerWidth < 768

  const validTransactionsIds = validTransactions?.map((tx) => tx.id)
  const validTransactionsExist = validTransactionsIds?.length
  const totalAmount = validTransactions?.reduce((total: number, tx) => total + tx?.amount, 0)

  const requiredEgld = validTransactions?.length * 0.0002
  const requiredNativeFunds = chain === 'multiversx' ? requiredEgld : gas

  const sufficientTxFundsExist = totalAmount <= selectedTokenBalance
  const { rpcCount, incrementRpcCount } = useRpcCountStore(
    (state) => ({ rpcCount: state.count, incrementRpcCount: state.increment }),
    shallow,
  )

  useEffect(() => {
    const calculateGas = async () => {
      const gas = await getEvmGasEstimate(
        validTransactions,
        chain,
        network,
        selectedToken,
        initiator,
        rpcCount,
        incrementRpcCount,
      )
      setGas(gas)
    }
    if (
      chain &&
      chain !== 'multiversx' &&
      network &&
      selectedToken?.tokenIdentifier &&
      initiator &&
      validTransactionsExist
    ) {
      calculateGas()
    }
  }, [chain, network, selectedToken, validTransactionsIds?.length, initiator])

  const handleExecuteTransactions = async () => {
    try {
      setIsLoading(true)
      await executeSelectedTransactions(
        validTransactions,
        airdropState.signer,
        airdropState.initiator,
        selectedToken,
        airdropState.externalWallet,
        chain,
        network,
        isWalletConnected,
        rpcCount,
        incrementRpcCount,
      )
      setIsLoading(false)
      clearSelectedTransactions()
      toggleExecuteModal()
    } catch (error) {
      setIsLoading(false)
      console.error(error)
    }
  }

  return (
    <Modal
      open={showExecuteModal}
      onClose={toggleExecuteModal}
      aria-labelledby="confirm-modal"
      aria-describedby="confirm-execute-modal">
      <Card
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          height: '600px',
          maxHeight: '90%',
          width: '600px',
          maxWidth: '90%',
          background: '#181D38',
          color: '#fff',
          borderRadius: '16px',
        }}>
        <Box
          py={3}
          px={2}
          sx={{
            width: '100%',
            height: '100%',
            minHeight: '300px',
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            borderRadius: '8px',
            boxSizing: 'border-box',
          }}>
          <SectionTitle
            title={
              validTransactionsExist
                ? `Sending a total of ${new Intl.NumberFormat().format(totalAmount)}
                ${selectedToken?.name || 'tokens'} to the following receivers:`
                : 'No pending transactions are selected'
            }
          />
          <Typography
            variant="h6"
            color={nativeCurrencyBalance < requiredNativeFunds ? 'error' : 'dark'}
            mt={2}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              textAlign: 'center',
              fontFamily: 'SpaceGrotesk, sans-serif',
            }}>
            {validTransactionsExist ? (
              <span>
                A total of ~ {requiredNativeFunds?.toFixed(12)}{' '}
                {networksData?.[chain]?.[network]?.nativeCurrency} is required for fees.
              </span>
            ) : null}
          </Typography>
          <Typography
            variant="h6"
            color={nativeCurrencyBalance < requiredNativeFunds ? 'error' : 'dark'}
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'center',
              textAlign: 'center',
              fontFamily: 'SpaceGrotesk, sans-serif',
            }}>
            {validTransactionsExist ? (
              <>
                {nativeCurrencyBalance < requiredNativeFunds ? (
                  <span>
                    You need ~ {requiredEgld - nativeCurrencyBalance} more{' '}
                    {networksData?.[chain]?.[network]?.nativeCurrency}.
                  </span>
                ) : null}
              </>
            ) : null}
          </Typography>
          <div className="txListContainer">
            {validTransactionsExist ? (
              <PerfectScrollbar>
                <ListGroup as="ol" numbered>
                  {validTransactions.map((tx) => (
                    <ListGroup.Item as="li" key={tx?.id}>
                      <div>
                        <span className="transaction-summary-item">
                          {formatAddressEllipsisShort(tx?.receiver)}
                        </span>
                        <span className="dotted-line"></span>
                        <span className="transaction-amount transaction-summary-item">
                          {new Intl.NumberFormat().format(tx?.amount)} {selectedToken?.name}
                        </span>
                      </div>
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </PerfectScrollbar>
            ) : null}
          </div>
          {sufficientTxFundsExist ? null : (
            <div className="requiredFundsInfoContainer">
              <span className="requiredFundsInfoText">
                You don't have enough {selectedToken?.name} for the transactions.
              </span>
            </div>
          )}
          <Box
            mt={4}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'row',
              width: '100%',
              gap: '40px',
            }}>
            <UiButton name="Cancel" size="M" onClick={toggleExecuteModal} type="secondary" />
            <Button
              className="btn btn-primary"
              color="success"
              size="medium"
              variant="contained"
              onClick={handleExecuteTransactions}
              disabled={
                isLoading ||
                !validTransactionsExist ||
                requiredEgld > nativeCurrencyBalance ||
                !sufficientTxFundsExist
              }>
              Execute
              {isLoading ? (
                <CircularProgress color="info" size="2rem" sx={{ marginLeft: '1rem' }} />
              ) : null}
            </Button>
          </Box>
        </Box>
      </Card>
    </Modal>
  )
}
